/**
 * Mobile Bottom Navigation Component
 * 
 * Provides bottom navigation bar for mobile devices with:
 * - 5 main quick-access buttons: Dashboard, Receitas, Despesas Futuras, Despesas Mensais, Despesas do Dia a Dia
 * - Drawer with full menu on the right side (hamburger icon)
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ArrowUpRight,
  Target,
  Receipt,
  ShoppingCart,
  Menu,
  Moon,
  Sun,
  LogOut,
  FileText,
  Users,
  LifeBuoy,
  BookOpen,
  FolderOpen,
  Video,
  UserCog,
  CalendarDays,
  Lock,
  Info,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { usePlanFeatures } from '@/hooks/use-plan-features';
import { usePlanNamesWithFeature } from '@/hooks/use-public-plans';
import { useUnreadTicketCount } from '@/hooks/use-support';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MobileBottomNavProps {
  onLogout: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  user: {
    name?: string;
    email?: string;
    avatar_url?: string;
  } | null;
}

// Main bottom nav items (the 5 required quick-access buttons)
const mainNavItems = [
  { id: 'dashboard', title: 'Dashboard', shortTitle: 'Home', icon: LayoutDashboard, path: '/financial', featureRequired: 'dashboard-financeiro' },
  { id: 'revenues', title: 'Receitas', shortTitle: 'Receitas', icon: ArrowUpRight, path: '/financial/revenues', featureRequired: 'gestao-de-receitas' },
  { id: 'future-expenses', title: 'Desp. Futuras', shortTitle: 'Futuras', icon: Target, path: '/financial/future-expenses', featureRequired: 'despesas-futuras' },
  { id: 'monthly-expenses', title: 'Desp. Mensais', shortTitle: 'Mensais', icon: Receipt, path: '/financial/monthly-expenses', featureRequired: 'despesas-mensais' },
  { id: 'daily-expenses', title: 'Dia a Dia', shortTitle: 'Dia a Dia', icon: ShoppingCart, path: '/financial/daily-expenses', featureRequired: 'despesas-do-dia-a-dia' },
];

// All navigation groups for the drawer
const allNavigationGroups = [
  {
    title: 'Dashboard',
    items: [{ id: 'financial-dashboard', title: 'Dashboard', icon: LayoutDashboard, path: '/financial', featureRequired: 'dashboard-financeiro' }],
  },
  {
    title: 'Minhas Finanças',
    items: [
      { id: 'revenues', title: 'Receitas', icon: ArrowUpRight, path: '/financial/revenues', featureRequired: 'gestao-de-receitas' },
      { id: 'future-expenses', title: 'Despesas Futuras', icon: Target, path: '/financial/future-expenses', featureRequired: 'despesas-futuras' },
      { id: 'monthly-expenses', title: 'Despesas Mensais', icon: Receipt, path: '/financial/monthly-expenses', featureRequired: 'despesas-mensais' },
      { id: 'daily-expenses', title: 'Despesas do Dia a Dia', icon: ShoppingCart, path: '/financial/daily-expenses', featureRequired: 'despesas-do-dia-a-dia' },
      { id: 'reports', title: 'Relatórios', icon: FileText, path: '/financial/reports', featureRequired: 'relatorios-financeiros' },
    ],
  },
  {
    title: 'Aprendizado',
    items: [
      { id: 'materials', title: 'Materiais de Apoio', icon: FolderOpen, path: '/learn/materials', featureRequired: 'materiais-de-apoio' },
      { id: 'videos', title: 'Vídeo-aulas', icon: Video, path: '/learn/videos', featureRequired: 'video-aulas' },
      { id: 'blog', title: 'Blog', icon: BookOpen, path: '/learn/blog', featureRequired: 'blog' },
    ],
  },
  {
    title: 'Conta',
    items: [
      { id: 'my-account', title: 'Minha Conta', icon: UserCog, path: '/account', featureRequired: 'minha-conta' },
      { id: 'agents', title: 'Agentes', icon: Users, path: '/financial/agents', featureRequired: 'agentes' },
      { id: 'budget-period', title: 'Período Orçamentário', icon: CalendarDays, path: '/account/budget-period', featureRequired: 'minha-conta' },
      { id: 'support', title: 'Suporte', icon: LifeBuoy, path: '/support', featureRequired: 'suporte' },
      { id: 'ajuda', title: 'Central de ajuda', icon: Info, path: '/support/help', featureRequired: null },
    ],
  },
];

const tutorialDrawerNavIds: Record<string, string> = {
  'financial-dashboard': 'nav-dashboard',
  'reports': 'nav-reports',
  'my-account': 'nav-my-account',
  'agents': 'nav-agents',
  'budget-period': 'nav-budget-period',
  'support': 'nav-support',
  'ajuda': 'nav-help-center',
};

const formatNotificationCount = (count: number) => (count > 99 ? '99+' : String(count));

function SupportNotificationBadge({
  count,
  className,
}: Readonly<{
  count: number;
  className?: string;
}>) {
  if (count <= 0) return null;

  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold leading-none text-white shadow-sm',
        className
      )}
    >
      {formatNotificationCount(count)}
    </span>
  );
}

export function MobileBottomNav({ onLogout, darkMode, onToggleDarkMode, user }: Readonly<MobileBottomNavProps>) {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);
  const [blockedItemTitle, setBlockedItemTitle] = useState('');
  const [blockedItemFeature, setBlockedItemFeature] = useState<string | null>(null);
  const { hasFeature, subscription } = usePlanFeatures();
  // Planos reais (de /public/plans) que incluem a funcionalidade bloqueada —
  // nunca sugerir nomes de planos fixos no código.
  const blockedFeaturePlanNames = usePlanNamesWithFeature(blockedItemFeature);
  const { data: unreadSupportData } = useUnreadTicketCount();
  const unreadSupportCount = unreadSupportData?.unread_count ?? 0;

  const checkAccess = (featureSlug: string | null): { hasAccess: boolean; isLocked: boolean } => {
    if (!featureSlug) return { hasAccess: true, isLocked: false };
    const hasAccess = hasFeature(featureSlug);
    return { hasAccess, isLocked: !hasAccess };
  };

  const handleBlockedClick = (itemTitle: string, featureSlug: string | null) => {
    setBlockedItemTitle(itemTitle);
    setBlockedItemFeature(featureSlug);
    setBlockedModalOpen(true);
  };

  const handleNavClick = (path: string, featureRequired: string | null, title: string) => {
    const { isLocked } = checkAccess(featureRequired);
    if (isLocked) {
      handleBlockedClick(title, featureRequired);
      return;
    }
    if (path !== '#') {
      navigate(path);
      setDrawerOpen(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/financial') {
      return location.pathname === '/financial' || location.pathname === '/dashboard';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border md:hidden safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-1">
          {mainNavItems.map((item) => {
            const { isLocked } = checkAccess(item.featureRequired);
            const active = isActive(item.path);
            
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavClick(item.path, item.featureRequired, item.title)}
                data-tour-id={[
                  'dashboard',
                  'revenues',
                  'future-expenses',
                  'monthly-expenses',
                  'daily-expenses',
                ].includes(item.id)
                  ? `nav-${item.id}`
                  : undefined}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 h-full py-1 px-0.5 transition-colors',
                  active
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                  isLocked && 'opacity-60'
                )}
                aria-label={item.title}
              >
                <div className="relative">
                  <item.icon className={cn('h-5 w-5', active && 'text-primary')} />
                  {isLocked && (
                    <Lock className="absolute -top-1 -right-1 h-2.5 w-2.5 text-amber-500" />
                  )}
                </div>
                <span className={cn(
                  'text-[10px] mt-0.5 font-medium truncate max-w-full',
                  active && 'text-primary'
                )}>
                  {item.shortTitle}
                </span>
              </button>
            );
          })}
          
          {/* Menu Drawer Trigger */}
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="flex flex-col items-center justify-center flex-1 h-full py-1 px-0.5 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Menu"
              >
                <div className="relative">
                  <Menu className="h-5 w-5" />
                  {unreadSupportCount > 0 && (
                    <SupportNotificationBadge
                      count={unreadSupportCount}
                      className="absolute -right-2 -top-2 h-4 min-w-4 px-1 text-[9px]"
                    />
                  )}
                </div>
                <span className="text-[10px] mt-0.5 font-medium">Menu</span>
              </button>
            </SheetTrigger>
            
            <SheetContent side="right" className="w-[300px] p-0 flex flex-col">
              <SheetHeader className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <SheetTitle className="flex items-center gap-2">
                    <img
                      src="/logo-librattum.png"
                      alt="Librattum"
                      className="h-6"
                    />
                  </SheetTitle>
                </div>
              </SheetHeader>
              
              {/* User Info */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 border-2 border-border">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                      {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {user?.name ?? 'Usuário'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Navigation Groups */}
              <div className="flex-1 overflow-y-auto py-2">
                {allNavigationGroups.map((group, groupIndex) => (
                  <div key={group.title} className={cn(groupIndex > 0 && 'mt-4')}>
                    <div className="px-4 py-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.title}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {group.items.map((item) => {
                        const { isLocked } = checkAccess(item.featureRequired);
                        const active = isActive(item.path);
                        const showSupportBadge = item.id === 'support' && unreadSupportCount > 0;
                        
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleNavClick(item.path, item.featureRequired, item.title)}
                            data-tour-id={tutorialDrawerNavIds[item.id]}
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors',
                              active
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                              isLocked && 'opacity-70'
                            )}
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="flex-1 text-left">{item.title}</span>
                            {showSupportBadge && (
                              <SupportNotificationBadge count={unreadSupportCount} />
                            )}
                            {isLocked && (
                              <Lock className="h-3 w-3 text-amber-500 shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Footer Actions */}
              <div className="p-4 border-t border-border mt-auto">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onToggleDarkMode}
                    className="flex-1"
                  >
                    {darkMode ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                    {darkMode ? 'Claro' : 'Escuro'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onLogout}
                    className="flex-1 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Blocked Feature Modal */}
      <Dialog open={blockedModalOpen} onOpenChange={setBlockedModalOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle>Funcionalidade Bloqueada</DialogTitle>
            </div>
            <DialogDescription className="pt-4">
              A funcionalidade <strong className="text-foreground">{blockedItemTitle}</strong> não está disponível no seu plano atual.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Seu plano atual:</span>
                <span className="font-medium">{subscription?.plan_name || 'Sem plano ativo'}</span>
              </div>
              {blockedFeaturePlanNames.length > 0 && (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Disponível em:</span>
                  <span className="font-medium text-amber-600 text-right">
                    {blockedFeaturePlanNames.join(', ')}
                  </span>
                </div>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mt-4">
              Atualize seu plano para desbloquear esta e outras funcionalidades exclusivas.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setBlockedModalOpen(false)}
              className="w-full sm:w-auto"
            >
              Fechar
            </Button>
            <Button
              onClick={() => {
                setBlockedModalOpen(false);
                navigate('/account?tab=plan');
              }}
              className="w-full sm:w-auto"
            >
              Ver Meus Planos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MobileBottomNav;
